import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useMutation } from '@tanstack/react-query';
import { updateApproval } from '@/api/approvals';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';
import { useAppDispatch } from '@/store';
import { notifyUser } from '@/store/Toast';
import type { UpdateApprovalPayload } from '@/types/Approvals';
import type { ReactSelectOption } from '@/types/Constants';
import { PaidFromSelect } from './PaidFromSelect';

type ApproveForm = {
  note: string;
  paidFrom: ReactSelectOption;
};

type ApproveModalProps = {
  projectId: string;
  approvalId: number;
  isBudget?: boolean;
  title: string;
  onCancel?: () => void;
  onSubmitted?: () => void;
};

export function ApproveHookForm({ projectId, approvalId, onCancel, isBudget, onSubmitted, title }: ApproveModalProps) {
  const dispatch = useAppDispatch();

  const updateApprovalMutation = useMutation(async (payload: UpdateApprovalPayload) => updateApproval(payload), {
    onSuccess: () => {
      onSubmitted?.();
      onCancel?.();
    },
    onError: () => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to update Approval',
          toastType: 'failed',
        })
      );
    },
  });

  const handleSubmit = ({ note, paidFrom }: ApproveForm) => {
    updateApprovalMutation.mutate({
      id: approvalId,
      status: 'APPROVED',
      note,
      ...(isBudget && { meta: { paidFrom: paidFrom.value } }),
    });
  };

  return (
    <HookForm onSubmit={handleSubmit}>
      <p className='text-center'>Are you sure you want to Approve this {title} change order?</p>
      <p className='text-center text-primaryColor-normal py-2'>Please confirm that you would like to make these changes </p>
      <div className='mt-5 space-y-5'>
        {isBudget && <PaidFromSelect projectId={projectId} />}
        <HookTextarea name='note' label='Approval Note' />
      </div>
      <div className='flex gap-2 justify-end w-full mt-5 border-t-2 pt-4'>
        <Button label='Cancel' btnStyle='btn-neutral' btnType='button' disabled={updateApprovalMutation.isLoading} onClick={onCancel} />
        <Button
          label={`${updateApprovalMutation.isLoading ? 'Please Wait...' : 'Confirm'}`}
          btnStyle='btn-primary'
          btnType='submit'
          disabled={updateApprovalMutation.isLoading}
        />
      </div>
    </HookForm>
  );
}

export const ApproveModal = NiceModal.create(
  ({ projectId, approvalId, isBudget, onSubmitted, title }: Omit<ApproveModalProps, 'onCancel'>) => {
    const { visible, hide } = useModal();

    if (!visible) return null;

    return (
      <Modal>
        <HookFormProvider>
          <ApproveHookForm
            title={title}
            projectId={projectId}
            approvalId={approvalId}
            onCancel={hide}
            isBudget={isBudget}
            onSubmitted={onSubmitted}
          />
        </HookFormProvider>
      </Modal>
    );
  }
);
